import { identity, pipe, Either, Runtime } from "fp";

import React from "react";

import { Asset } from "~/Asset";
import { Mobile, Pencil, Truck } from "~/Icon";
import * as Layout from "~/Layout";

/** The origin of an `Event` */
export type Source = Partial<Asset> & { readonly type?: Type.Type };

export type Revision = ReturnType<typeof revision>;

export const revision = (): Source => ({ type: Type.revision() });

export const isRevision = ({ type }: Source): boolean =>
  type === Type.revision();

export const toDescription = (source: Source): string | undefined =>
  source.asset?.name ??
  (source.asset?.id && `Asset ${source.asset.id}`) ??
  (source.type && Type.toDescription(source.type));

export const Component: React.Fn<Source & { readonly icon?: boolean }> = ({
  icon = false,
  ...source
}) =>
  source.asset ? (
    <Asset.Link asset={source.asset}>{toDescription(source)}</Asset.Link>
  ) : (
    <>
      {icon && <Icon.Component {...source} />}
      {toDescription(source)}
    </>
  );

namespace Icon {
  export const Component: Component<Source> = (source) =>
    !source.type ? null : (
      <Layout.Inline.Component right>
        <Type.Icon.Component {...source} />
      </Layout.Inline.Component>
    );
}

export namespace Type {
  export type Type = ReturnType<
    typeof application | typeof tracker | typeof revision
  >;

  export const application = () => "APPLICATION" as const;
  export const tracker = () => "TRACKER" as const;
  export const revision = () => "REVISION" as const;

  export const is = (type?: unknown): type is Type =>
    type === application() || type === tracker() || type === revision();

  export const runtime = () =>
    new Runtime.Type(
      "Event.Source.Type",
      is,
      (input, context) =>
        pipe(
          input,
          Either.fromPredicate(is, identity),
          Either.map(Runtime.success),
          Either.getOrElse(() => Runtime.failure(input, context))
        ),
      identity
    );

  export const toDescription = (type: Type): string => {
    switch (type) {
      case application():
        return "Application";
      case tracker():
        return "Application";
      case revision():
        return "Revision";
    }
  };

  export namespace Icon {
    export const Component: Component<Source> = ({ type }) => {
      switch (type) {
        case application():
          return <Mobile faded />;
        case tracker():
          return <Truck faded />;
        case revision():
          return <Pencil faded />;
        default:
          return null;
      }
    };
  }
}

export namespace Sources {
  export type Sources = {
    readonly original?: Source;
    readonly current: Source;
  };

  export const original = ({ original, current }: Sources): Source =>
    original ?? current;
}
